import { isOutsideBerthReservation } from 'src/components/dispatch/berthReservationAlert';
import { buildShipperBreakdown } from 'src/components/dispatch/detailPanel/shipperBreakdown';
import { buildDisplayInfoFields } from 'src/components/dispatch/unassignedDisplay/displayInfoFields';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { DeliveryCard, DispatchCard, LoadingCard, UnassignedItem, Vehicle } from 'src/entities/dispatch';
import { PlanningsLegRunOperationEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import { anyMainWarehouse } from './placeKeys';
import { VehicleRowCardVM, VehicleRowVM } from './vehicleRowVM';

const JP_WEEKDAY = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'];

/** ISO 日時文字列 → "4月 10日 (金曜日)" */
function formatJpDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}月 ${d.getDate()}日 (${JP_WEEKDAY[d.getDay()]})`;
}

/** ISO 日時文字列 → "11:15" */
function formatHm(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return datetimeDecorator.toHourMinutes(d) ?? '';
}

/** start/end の ISO 文字列 → "11:15〜12:00" */
function formatTimeRange(startIso?: string | null, endIso?: string | null): string {
  const s = formatHm(startIso);
  const e = formatHm(endIso);
  if (s && e) return `${s}〜${e}`;
  return s || e || '';
}

/** legRun 配列の全 designated_truck_* / item_klass を制約タグ配列に変換 */
function buildConstraintTags(legRuns: ShipmentOrderLegRunForPlanEntity[]): string[] {
  const set = new Set<string>();
  legRuns.forEach((lr) => {
    lr.designated_truck_klasses?.forEach((v) => set.add(v));
    lr.designated_truck_car_models?.forEach((v) => set.add(v));
    lr.designated_truck_features?.forEach((v) => set.add(v));
    lr.designated_truck_floor_specifications?.forEach((v) => set.add(String(v)));
    lr.designated_truck_loading_platform_heights?.forEach((v) => set.add(String(v)));
    lr.designated_truck_loading_platform_lengths?.forEach((v) => set.add(String(v)));
    lr.designated_truck_loading_platform_widths?.forEach((v) => set.add(String(v)));
  });
  return Array.from(set).filter(Boolean);
}

/** legRun 配列から一意な shipper_name 一覧 */
function buildShippers(legRuns: ShipmentOrderLegRunForPlanEntity[]): string[] {
  const set = new Set<string>();
  legRuns.forEach((lr) => {
    if (lr.shipper_name) set.add(lr.shipper_name);
  });
  return Array.from(set);
}

/** legRun 配列から unique な shipment_order_id 数 = 伝票枚数 */
function countInvoices(legRuns: ShipmentOrderLegRunForPlanEntity[]): number | null {
  const set = new Set<number>();
  legRuns.forEach((lr) => {
    if (typeof lr.shipment_order_id === 'number') set.add(lr.shipment_order_id);
  });
  return set.size > 0 ? set.size : null;
}

/** 指定時間 (specified_datetimes_attributes) を "HH:MM - HH:MM" 形式で */
function formatDesignatedWindow(
  legRuns: ShipmentOrderLegRunForPlanEntity[],
  side: 'loading' | 'unloading',
): string | null {
  const ranges: string[] = [];
  legRuns.forEach((lr) => {
    const list = side === 'loading'
      ? lr.loading_specified_datetimes_attributes
      : lr.unloading_specified_datetimes_attributes;
    (list ?? []).forEach((d) => {
      const s = formatHm(d.start_at);
      const e = formatHm(d.end_at);
      if (s && e) ranges.push(`${s} - ${e}`);
    });
  });
  return Array.from(new Set(ranges)).join(', ') || null;
}

/** operations から schedule の (arrivalAt 最小, departureAt 最大, durationSeconds 最大) を導出 */
function aggregateOperationSchedule(ops: PlanningsLegRunOperationEntity[]) {
  if (ops.length === 0) {
    return { arrivalAt: null, departureAt: null, durationSec: 0 };
  }
  let minArrival: string | null = null;
  let maxDeparture: string | null = null;
  let maxDuration = 0;
  ops.forEach((op) => {
    if (op.arrivalAt && (minArrival == null || op.arrivalAt < minArrival)) minArrival = op.arrivalAt;
    if (op.departureAt && (maxDeparture == null || op.departureAt > maxDeparture)) maxDeparture = op.departureAt;
    if (op.operationDurationSeconds > maxDuration) maxDuration = op.operationDurationSeconds;
  });
  return { arrivalAt: minArrival, departureAt: maxDeparture, durationSec: maxDuration };
}

/**
 * 指定時間の最小 start / 最大 end を ms で返す。未設定なら null。
 * loading_specified_datetimes_attributes / unloading_specified_datetimes_attributes は
 * 複数の時間窓を持つ可能性があるので、V3Plans の loading_start_at/end_at と同じく
 * min(start_at) / max(end_at) を取る（全窓を包含する最広域）。
 */
function getDesignatedWindowMs(
  lr: ShipmentOrderLegRunForPlanEntity,
  side: 'loading' | 'unloading',
): { minStartMs: number; maxEndMs: number } | null {
  const list = side === 'loading'
    ? lr.loading_specified_datetimes_attributes
    : lr.unloading_specified_datetimes_attributes;
  if (!list || list.length === 0) return null;
  let minStartMs = Number.POSITIVE_INFINITY;
  let maxEndMs = Number.NEGATIVE_INFINITY;
  list.forEach((d) => {
    if (d.start_at) {
      const s = new Date(d.start_at).getTime();
      if (!Number.isNaN(s) && s < minStartMs) minStartMs = s;
    }
    if (d.end_at) {
      const e = new Date(d.end_at).getTime();
      if (!Number.isNaN(e) && e > maxEndMs) maxEndMs = e;
    }
  });
  if (!Number.isFinite(minStartMs) || !Number.isFinite(maxEndMs)) return null;
  return { minStartMs, maxEndMs };
}

/**
 * 指定時間超過の判定（V3Plans の PlanningsOperationPlace.isOutsideSpecifiedTime 相当）。
 * 指定時間が設定されている operation で
 * 「到着が最遅 end_at より遅い」または「出発が最早 start_at より早い」なら true。
 * 指定時間が未設定 (list が空) の場合は判定対象外として false。
 */
function isOutsideSpecifiedTime(
  ops: PlanningsLegRunOperationEntity[],
  legRuns: ShipmentOrderLegRunForPlanEntity[],
): boolean {
  if (ops.length === 0) return false;

  // V3Plans の PlanningsOperationPlace と同じく、同じ地点 (= 同じカード) の
  // 全 operation を集約した place.arrivalAt (min) / place.departureAt (max) で判定する。
  // 各 op 個別の arrivalAt/departureAt を使うと、同一カード内で op ごとに違反判定が割れてしまう。
  let placeArrivalMs = Number.POSITIVE_INFINITY;
  let placeDepartureMs = Number.NEGATIVE_INFINITY;
  ops.forEach((op) => {
    if (op.arrivalAt) {
      const a = new Date(op.arrivalAt).getTime();
      if (!Number.isNaN(a) && a < placeArrivalMs) placeArrivalMs = a;
    }
    if (op.departureAt) {
      const d = new Date(op.departureAt).getTime();
      if (!Number.isNaN(d) && d > placeDepartureMs) placeDepartureMs = d;
    }
  });
  if (!Number.isFinite(placeArrivalMs) || !Number.isFinite(placeDepartureMs)) return false;

  const legRunById = new Map<number, ShipmentOrderLegRunForPlanEntity>();
  legRuns.forEach((lr) => {
    if (lr.id != null) legRunById.set(lr.id, lr);
  });

  return ops.some((op) => {
    const lr = legRunById.get(op.shipmentOrderLegRunId);
    if (!lr) return false;
    const side = op.action === '積' ? 'loading' : 'unloading';
    const win = getDesignatedWindowMs(lr, side);
    if (!win) return false;
    return (win.maxEndMs - placeArrivalMs) < 0 || (placeDepartureMs - win.minStartMs) < 0;
  });
}


/** legRun の memo を結合（空文字除外） */
function buildRemarks(legRuns: ShipmentOrderLegRunForPlanEntity[]): string | null {
  const memos = legRuns
    .map((lr) => (lr.memo ?? '').trim())
    .filter((m) => m.length > 0);
  const uniq = Array.from(new Set(memos));
  return uniq.length > 0 ? uniq.join(' / ') : null;
}

export type VehicleInfoExtra = {
  partnerCompanyName: string | null;
  /** truck.name (例: "5号車") — 左リスト 2 段目 */
  truckName: string | null;
  /** 当日値 (shift_trucks.truck_number)。最優先で表示する車番。 */
  shiftTruckNumber: string | null;
  /** マスタ由来の車番フォールバック (truck.truck_number) */
  masterTruckNumber: string | null;
  /** 旧名称: license_plate_value だが互換のため残す。表示には使わない */
  truckLabel: string | null;
  defaultDriverName: string | null;
  /** 当日の勤務開始時刻 (HH:mm)。delivery.startAt 由来。 */
  shiftStart: string | null;
  /** 当日の勤務終了時刻 (HH:mm)。delivery.endAt 由来。 */
  shiftEnd: string | null;
  /** 労働可能時間 (秒)。delivery.workingAvailableDurationSeconds 由来。 */
  workingAvailableSeconds: number | null;
  /** ドライバー id (差し替え時に使う) */
  driverId: number | null;
  /** 配車枠の shift_truck id (差し替え API のターゲット) */
  shiftTruckId: number | null;
  capacityCases: number | null;
  capacityQuantity: number | null;
  capacityKg: number | null;
  capacityM3: number | null;
  /** 車両カラー (truck.color / delivery.truckColor 由来)。未設定なら null */
  truckColor: string | null;
  /** 委託先 (パートナー会社) マスタ id */
  partnerCompanyId: number | null;
  /** 委託先 (パートナー会社) カラー。temporary truck なら null */
  partnerCompanyColor: string | null;
  /** 車両タイプ (truck.klass。例: "2t")。未設定なら null */
  truckKlass: string | null;
};

export type RotationRow = {
  rowId: string;
  vehicleId: string;
  truckId: number;
  /**
   * この行に乗務するドライバー。1 台に複数のドライバーが同じ時間帯で乗る勤務があるため、
   * 車両単位の代表 1 人（Vehicle.driverId）とは別に行が持つ。名前の解決は driver 一覧を
   * 持つ呼び出し側の責務。
   */
  driverId: number | null;
  /** この行の勤務 (shift_truck)。delivery と 1 対 1 で、ドライバー変更の対象になる */
  shiftTruckId: number | null;
  rotationNumber: number;
  totalRotations: number;
  /**
   * 「◯回転目」バッジに出す確定済みの番号。行を落とす絞り込み（回転・選択中のみ）が
   * 落とす前に付ける。位置で数え直すと、1 回転目を外したときに 2 回転目が無印
   * （= 1 回転目）に見える。付いていない行は位置で数える。
   */
  displayRotationNumber?: number;
  /** このrotation配下で表示するカード列。dnd-kit の SortableContext に使う。 */
  cards: DispatchCard[];
  /** 元の VehicleRowVM */
  source: VehicleRowVM;
  /**
   * 重なり/表示判定のため、実配送(delivery)カードだけ separately 保持。
   *
   * 読み始める前に 2 点。現状この値を読む本体コードは 0 件（展開パネルへ渡るのは
   * `cards` の方。VehicleListRow の ExpandedRowPanel を参照）。そして値の広さが経路で
   * 違う — ここで組む行は loading 以外の全カード（no-delivery / fr-template を含む）、
   * `applyOverrideRowCards` を通った行はその 2 つを除いた実配送カードだけ。
   * 描き始めるなら、まずこの食い違いを揃えること。
   */
  deliveryCards: DeliveryCard[];
};

export type VehicleRowGroup = {
  vehicle: Vehicle;
  rows: RotationRow[];
  /**
   * このグループの勤務 (shift_truck.id = delivery.id)。車両・車番・ドライバーの
   * 差し替え先。1 台に複数の勤務が乗るので truck からは引けない。
   * 固定ルート由来の合成行しか無いグループは実体が無いので null。
   */
  shiftTruckId: number | null;
};

export function vehicleIdFromTruckId(truckId: number): string {
  return `truck-${truckId}`;
}

/**
 * 行の id。
 *
 * deliveryId を付けるのは、1 台のトラックに複数のドライバーが同じ時間帯で乗る勤務
 * （古い「勤務計画」で作れる）があるため。各ドライバーの shift_truck が別の delivery に
 * なり、operations を持たないと全員が rotationNumber=1 になるので、truckId と
 * rotationNumber だけでは 1 人ぶんしか残らない。
 *
 * 先頭を `vehicle-row-<truckId>-` のまま保つのは、ここから truckId を取り出している
 * 呼び出し側（ConvenienceDispatches/Presenter.tsx の resolveShiftTruckIdFromRowId）と
 * `vehicle-row-` の前方一致で drop 先を判定している D&D を壊さないため。
 */
export function rowIdFromTruck(truckId: number, rotationNumber: number, deliveryId?: number): string {
  const base = `vehicle-row-${truckId}-${rotationNumber}`;
  return deliveryId == null ? base : `${base}-${deliveryId}`;
}

export function loadingCardId(truckId: number, rotationNumber: number, placeKey: string, index = 0): string {
  // 同じ (truckId, rotation, placeKey) で複数回現れる可能性があるので index で一意化する
  return `loading-${truckId}-${rotationNumber}-${placeKey}-${index}`;
}

export function noDeliveryCardId(truckId: number, rotationNumber: number, placeKey: string, index = 0): string {
  // 同じ rotation 内に同名 stop が非連続で複数ある場合、placeKey は同一だが
  // mergeOrPushExtra でマージされない別カードとして残るので index で disambiguate する。
  return `noDelivery-${truckId}-${rotationNumber}-${placeKey}-${index}`;
}

export function deliveryCardId(
  legRunId: number | null | undefined,
  placeKey: string,
  index: number,
  truckId?: number,
  rotationNumber?: number,
): string {
  // 同じ legRun が複数 row のカードに同時に乗る (D&D の途中状態 / FR template 経由など) ことが
  // ありえるため、truck × rotation を含めて React key 衝突を防ぐ。
  if (legRunId != null) {
    if (truckId != null && rotationNumber != null) return `lr-${truckId}-${rotationNumber}-${legRunId}`;
    return `lr-${legRunId}`;
  }
  return `op-${placeKey}-${index}`;
}

function buildLoadingCard(
  source: VehicleRowCardVM,
  truckId: number,
  rotationNumber: number,
  cardIndex: number,
): LoadingCard {
  const firstLaneId = source.legRuns.find((lr) => lr.shipment_order_lane_id != null)?.shipment_order_lane_id ?? null;

  // 積地: 日付/時刻は operation.arrivalAt/departureAt（優先）、なければ legRun.loading_start_at/end_at
  const opSchedule = aggregateOperationSchedule(source.operations);
  const fallbackStart = source.legRuns[0]?.loading_start_at ?? null;
  const fallbackEnd = source.legRuns[0]?.loading_end_at ?? null;
  const arrivalIso = opSchedule.arrivalAt ?? fallbackStart;
  const departureIso = opSchedule.departureAt ?? fallbackEnd;
  // 作業時間: operationDurationSeconds を優先、fallback で loading_staying_seconds
  const loadingStayingMax = source.legRuns.reduce(
    (max, r) => Math.max(max, r.loading_staying_seconds ?? 0),
    0,
  );
  const durationSecEffective = opSchedule.durationSec > 0 ? opSchedule.durationSec : loadingStayingMax;
  const workDurationMin = durationSecEffective > 0 ? Math.round(durationSecEffective / 60) : null;

  // 重量・体積は legRun に値が 1 つでもあれば合計する (合計 0 でも `0kg` として残す)。
  // 全 legRun が null のときだけ null を返して "--kg" / "--m³" 表示にする。
  const hasAnyWeight = source.legRuns.some((r) => r.item_total_weight_kg != null);
  const hasAnyVolume = source.legRuns.some((r) => r.item_total_volume_m3 != null);
  const weightSum = hasAnyWeight
    ? source.legRuns.reduce((s, r) => s + (r.item_total_weight_kg ?? 0), 0)
    : null;
  const volumeSum = hasAnyVolume
    ? source.legRuns.reduce((s, r) => s + (r.item_total_volume_m3 ?? 0), 0)
    : null;

  return {
    id: loadingCardId(truckId, rotationNumber, source.placeKey, cardIndex),
    type: 'loading',
    round: rotationNumber,
    locationName: source.placeName,
    timeConstraint: source.timeLabel,
    placeKey: source.placeKey,
    isMainWarehouse: anyMainWarehouse('loading', source.legRuns),
    legRunIds: source.legRuns.map((lr) => lr.id).filter((x): x is number => typeof x === 'number'),
    laneId: firstLaneId,
    hasAlert: isOutsideSpecifiedTime(source.operations, source.legRuns),
    hasBerthAlert: isOutsideBerthReservation(source.operations, source.legRuns),
    // 詳細ポップ用の補助情報 (積地側)
    scheduledDate: formatJpDate(arrivalIso),
    scheduledTime: formatTimeRange(arrivalIso, departureIso),
    workDuration: workDurationMin,
    invoiceCount: countInvoices(source.legRuns),
    remarks: buildRemarks(source.legRuns) ?? undefined,
    shippers: buildShippers(source.legRuns),
    designatedTimeWindow: formatDesignatedWindow(source.legRuns, 'loading'),
    constraints: buildConstraintTags(source.legRuns),
    cases: source.itemCount,
    weight: weightSum,
    volume: volumeSum,
    address: source.legRuns[0]?.loading_address ?? undefined,
    geo: source.legRuns[0] && source.legRuns[0].loading_lat != null && source.legRuns[0].loading_lng != null
      ? { lat: source.legRuns[0].loading_lat, lng: source.legRuns[0].loading_lng }
      : undefined,
  };
}

function buildDeliveryCard(
  source: VehicleRowCardVM,
  truckId: number,
  rotationNumber: number,
  cardIndex: number,
): DeliveryCard {
  const lr = source.legRuns[0];
  const isNoDelivery = source.kind === 'no_delivery';
  const isTemplate = source.kind === 'template';
  const isInactive = isNoDelivery || isTemplate;
  // 同じ rotation 内に同名 stop が非連続で複数ある場合、mergeOrPushExtra でマージされず
  // placeKey が同一の別カードとして残るので template / noDelivery 側でも cardIndex で disambiguate する。
  const id = isNoDelivery
    ? noDeliveryCardId(truckId, rotationNumber, source.placeKey, cardIndex)
    : isTemplate
      ? `template-${truckId}-${rotationNumber}-${cardIndex}-${source.placeKey}`
      : deliveryCardId(lr?.id, source.placeKey, cardIndex, truckId, rotationNumber);

  // 1 card は複数 legRun のマージを含み得るので合計する。
  // 重量・体積は legRun に値が 1 つでもあれば合計する (合計 0 でも `0kg` として残す)。
  // 全 legRun が null のときだけ null を返して "--kg" / "--m³" 表示にする。
  const hasAnyWeight = source.legRuns.some((r) => r.item_total_weight_kg != null);
  const hasAnyVolume = source.legRuns.some((r) => r.item_total_volume_m3 != null);
  const weightSum = hasAnyWeight
    ? source.legRuns.reduce((s, r) => s + (r.item_total_weight_kg ?? 0), 0)
    : null;
  const volumeSum = hasAnyVolume
    ? source.legRuns.reduce((s, r) => s + (r.item_total_volume_m3 ?? 0), 0)
    : null;

  // 降地: 日付/時刻は operation.arrivalAt/departureAt（優先）、なければ legRun.unloading_start_at/end_at
  const opSchedule = aggregateOperationSchedule(source.operations);
  const arrivalIso = opSchedule.arrivalAt ?? lr?.unloading_start_at ?? null;
  const departureIso = opSchedule.departureAt ?? lr?.unloading_end_at ?? null;
  // 作業時間: operationDurationSeconds を優先、fallback で unloading_staying_seconds
  const unloadingStayingMax = source.legRuns.reduce(
    (max, r) => Math.max(max, r.unloading_staying_seconds ?? 0),
    0,
  );
  const durationSecEffective = opSchedule.durationSec > 0 ? opSchedule.durationSec : unloadingStayingMax;
  const workDurationMin = durationSecEffective > 0 ? Math.round(durationSecEffective / 60) : null;

  return {
    id,
    type: 'delivery',
    storeName: source.placeName,
    wasSplit: source.legRuns.some((r) => r.was_split),
    storeAddress: lr?.unloading_address,
    originAddress: lr?.loading_address,
    loadingPrefecture: lr?.loading_administrative_area_level1 ?? null,
    loadingCity: lr?.loading_administrative_area_level2 ?? null,
    unloadingPrefecture: lr?.unloading_administrative_area_level1 ?? null,
    unloadingCity: lr?.unloading_administrative_area_level2 ?? null,
    geo: lr && lr.unloading_lat != null && lr.unloading_lng != null
      ? { lat: lr.unloading_lat, lng: lr.unloading_lng }
      : undefined,
    cases: source.itemCount,
    todayCases: isNoDelivery ? null : source.itemCount,
    weight: weightSum,
    volume: volumeSum,
    status: isNoDelivery ? 'no-delivery' : isTemplate ? 'fr-template' : 'matched',
    isActive: !isInactive,
    timeConstraint: source.timeLabel,
    hasAlert: !isInactive && isOutsideSpecifiedTime(source.operations, source.legRuns),
    hasBerthAlert: !isInactive && isOutsideBerthReservation(source.operations, source.legRuns),
    origin: lr?.loading_name,
    destination: lr?.unloading_name,
    originTimeWindow: formatTimeRange(lr?.loading_start_at, lr?.loading_end_at),
    destTimeWindow: formatTimeRange(lr?.unloading_start_at, lr?.unloading_end_at),
    shippers: buildShippers(source.legRuns),
    shipperBreakdown: buildShipperBreakdown(source.legRuns),
    placeKey: source.placeKey,
    // 仮カード (template / no_delivery) は判定から外れているので false 固定でよい。
    // frStopType='loading' の仮カードも buildDeliveryCard を通るため kind で側を決める
    isMainWarehouse: source.kind === 'unloading' && anyMainWarehouse('unloading', source.legRuns),
    legRunIds: isNoDelivery
      ? undefined
      : source.legRuns.map((r) => r.id).filter((x): x is number => typeof x === 'number'),
    laneId: lr?.shipment_order_lane_id ?? null,
    scheduledDate: formatJpDate(arrivalIso),
    scheduledTime: formatTimeRange(arrivalIso, departureIso),
    workDuration: workDurationMin,
    invoiceCount: countInvoices(source.legRuns),
    remarks: buildRemarks(source.legRuns) ?? undefined,
    designatedTimeWindow: formatDesignatedWindow(source.legRuns, 'unloading'),
    constraints: buildConstraintTags(source.legRuns),
    ...buildDisplayInfoFields(source.legRuns),
    // 固定ルート由来 (template / no_delivery) のカードは元の stopType を持ち回す
    frStopType: (source.kind === 'template' || source.kind === 'no_delivery')
      ? source.stopType
      : undefined,
  };
}

/**
 * row の cards のうち、実 operation 由来 (kind: 'loading' | 'unloading') のものに含まれる
 * legRun id を unique で返す。template / no_delivery カードの候補 legRun は含めない。
 *
 * OFF / 選択行削除 / 回転削除 で「実際に未割当に戻る legRun」を集計する用途で使う。
 * template カードの `legRuns` は「同じ店舗名の未割当候補」を持つため、これを混ぜると
 * 「割り当てられている n 件の案件は未割当に戻ります」のような表現で誤集計となる。
 */
export function getMatchedLegRunIdsInRow(row: VehicleRowVM): number[] {
  const ids = new Set<number>();
  for (const c of row.cards) {
    if (c.kind !== 'loading' && c.kind !== 'unloading') continue;
    for (const lr of c.legRuns) {
      if (typeof lr.id === 'number') ids.add(lr.id);
    }
  }
  return Array.from(ids);
}

/**
 * ユーザーが × ボタンで非表示にした placeholder rotation を `VehicleRowGroup[]` から除外する。
 * 実配送 row (isPlaceholderRow=false) は hidden に含まれていても残す。
 * 同じ rotationNumber に後から実配送 row が現れたケースでも UI 上で復活させるのが狙い。
 */
export function filterHiddenPlaceholderRotations(
  rowGroups: VehicleRowGroup[],
  hiddenPlaceholderRotations: Map<number, Set<number>>,
): VehicleRowGroup[] {
  if (hiddenPlaceholderRotations.size === 0) return rowGroups;
  return rowGroups
    .map((g) => {
      // 勤務 (delivery) ごとに隠す。truckId で引くと、同じ truck の別の勤務の
      // 同じ回転番号まで消える（回転番号は勤務ごとに 1 から振るので必ず重なる）
      const deliveryId = g.rows
        .map((r) => r.source.extraOfDeliveryId ?? r.source.deliveryId)
        .find((id) => id > 0);
      const hidden = deliveryId != null ? hiddenPlaceholderRotations.get(deliveryId) : undefined;
      if (!hidden || hidden.size === 0) return g;
      return {
        ...g,
        rows: g.rows.filter((r) => !r.source.isPlaceholderRow || !hidden.has(r.rotationNumber)),
      };
    })
    .filter((g) => g.rows.length > 0);
}

/**
 * `buildVehicleRowGroups` が前回の結果を持ち回すための入れ物。呼び出し側が `useRef` で持つ。
 *
 * Why not 純関数のまま前回値を引数で受けて次のキャッシュを戻り値に足す: 戻り値の型を
 * 変えると 2 つの呼び出し元と `__tests__/adapter.test.ts` の 20 箇所を書き換える
 * ことになる。この入れ物への書き込みだけに副作用を閉じ込める。
 */
export type VehicleRowGroupsCache = {
  /** 前回渡された補助 Map。`null` は「まだ 1 度も呼ばれていない」 */
  extraMap: ReadonlyMap<number, VehicleInfoExtra> | null;
  /**
   * 前回渡された driverNameById。`undefined` は「呼び出し側が渡さなかった」という
   * 正当な値なので、初期状態はそれと重ならない `null` で表す（両者を区別できる
   * ようにするだけ。初回は byGroupKey が空なので、これが無くても誤再利用は起きない）。
   */
  driverNameById: ReadonlyMap<number, string> | undefined | null;
  byGroupKey: Map<string, { rows: VehicleRowVM[]; group: VehicleRowGroup }>;
};

export function createVehicleRowGroupsCache(): VehicleRowGroupsCache {
  return { extraMap: null, driverNameById: null, byGroupKey: new Map() };
}

/**
 * グループの入力 VM が前回と全部同じか。
 *
 * `byDelivery` の値は毎回新しく作る配列なので、配列そのものではなく**要素の参照**を比べる。
 */
function sameRows(a: VehicleRowVM[], b: VehicleRowVM[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((row, index) => row === b[index]);
}

/**
 * 再利用してよいときだけ、前回の groupKey → グループの対応を返す。
 *
 * Why not グループごとに extra / driver 名だけを比べる: 参照が変わったマスタの
 * どこが効くかはグループを組んでみないと分からず、判定の取りこぼしが
 * 「マスタを直しても表示が変わらない」という静かな不具合になる。取りこぼす側には
 * 倒せないので、補助 Map の参照が変わったら全部作り直す方に振る。
 *
 * この線引きの結果、効く経路と効かない経路がはっきり分かれる。補助 Map は両画面とも
 * deliveries 由来の useMemo なので（convenience の vehicleExtraMap は deps に
 * truckCapacityMap と truckDriverMap を持ち、そのどちらも deps が `[deliveries]`。
 * /map の extraMap は deps が `[deliveries, trucks, drivers]`）、
 * **サーバデータが再取得されると必ず新しい Map になり、全グループを作り直す**。
 *
 * - 効く: 回転の追加・削除のうち**追加回転を持たない勤務**のグループ（extraRotations の
 *   state 変更だけで再取得が無い）、カードの D&D 上書き（overrideRowCards は
 *   serverRowGroups の外側で被せる）、/map の停車地並べ替えプレビュー
 *   （stopOrderPreview → previewedSourceRows）
 * - 効かない: 配車の確定・リセットなど deliveries が変わる操作。ここは作り直しが 1 回
 *   走るだけで、ドラッグ中の 1 枚ごとの再描画は BoardInteractionStore
 *   (boardInteractionStore.ts) の購読が受け持つ
 *
 * Why not 追加回転を持つ勤務のグループも効かせる: mergeExtraRotations
 * (rotationEditing.ts) が placeholder 行を `extras.push({ ... })` で**毎回新規に**作るため、
 * その行を含むグループは sameRows が必ず false になる。行単位の構造共有は末尾の
 * `merged.map(...)` の段だけで、そこへ来る placeholder は既に新品なので参照は残らない。
 * 画面では「勤務 A に回転を追加 → 勤務 B に追加 → A の左行・中央行も描き直される」と出る。
 * 直すには placeholder 行を呼び出し間で使い回す必要があり、その deliveryId は rowId
 * （dnd-kit の id かつ overrideRowCards のキー）に入る = 挙動が見える面に触るので、
 * ここでは踏み込まず別 issue に切り出す。
 */
function reusableGroups(
  cache: VehicleRowGroupsCache | undefined,
  vehicleExtraMap: ReadonlyMap<number, VehicleInfoExtra>,
  driverNameById: ReadonlyMap<number, string> | undefined,
): VehicleRowGroupsCache['byGroupKey'] | undefined {
  if (!cache) return undefined;
  if (cache.extraMap !== vehicleExtraMap) return undefined;
  if (cache.driverNameById !== driverNameById) return undefined;
  return cache.byGroupKey;
}

/**
 * `VehicleRowVM[]` と補助マップから、
 * `VehicleRowGroup[]` (= truckId でグループ化された RotationRow[]) を作成する。
 *
 * Vehicle は truckId 単位で一意化され、cards は全 rotation のカード列を連結したもの。
 * RotationRow は (truckId, rotationNumber) 単位で、その rotation のみのカード列を持つ。
 */
export function buildVehicleRowGroups(
  rows: VehicleRowVM[],
  vehicleExtraMap: ReadonlyMap<number, VehicleInfoExtra>,
  /**
   * driverId → 氏名。勤務ごとのドライバー名を出すために使う。
   * vehicleExtraMap は truckId 代表なので、これが無いと 1 台に複数の勤務が乗るとき
   * 2 件目以降が 1 件目のドライバー名になる（詳細パネル・共通配送先が vehicle を読む）。
   */
  driverNameById?: ReadonlyMap<number, string>,
  /**
   * 前回の結果。渡すと、入力 VM も補助 Map も変わっていないグループは前回のオブジェクトを
   * そのまま返す（渡さなければ毎回作り直す）。行の memo を効かせるために要る。
   */
  cache?: VehicleRowGroupsCache,
): VehicleRowGroup[] {
  // 勤務 (delivery) ごとにまとめ、rows 配列の順序を維持する。
  //
  // truckId だけで束ねると、1 台に複数の勤務が乗るとき rotationNumber 順に並べ替えられて
  // 勤務が交互に混ざる（実測 /map/2026-07-03 の truck 149: 53835 → 54493 → 53835）。
  // /plans は勤務ごとにまとめており、そちらに揃える。
  //
  // 「回転を追加」の仮想行は実体が無く deliveryId が負のダミーなので、実在 delivery に
  // ぶら下げる（グループが分かれると配車できない行だけが浮く）。
  // 追加元は extraOfDeliveryId が持つ。これを見ないと truck の先頭 delivery に付き、
  // 2 人目のドライバーで押した回転が 1 人目の側に入る（報告の現象）。
  // 固定ルート由来の placeholder は extraOfDeliveryId を持たないので truck から引く。
  const realDeliveryIdByTruck = new Map<number, number>();
  for (const r of rows) {
    if (r.isPlaceholderRow) continue;
    if (!realDeliveryIdByTruck.has(r.truckId)) realDeliveryIdByTruck.set(r.truckId, r.deliveryId);
  }
  const deliveryIdOf = (r: VehicleRowVM) => {
    if (!r.isPlaceholderRow) return r.deliveryId;
    return r.extraOfDeliveryId ?? realDeliveryIdByTruck.get(r.truckId) ?? r.deliveryId;
  };
  const groupKeyOf = (r: VehicleRowVM) => `${r.truckId}:${deliveryIdOf(r)}`;

  const byDelivery = new Map<string, VehicleRowVM[]>();
  for (const r of rows) {
    const key = groupKeyOf(r);
    const bucket = byDelivery.get(key);
    if (bucket) bucket.push(r);
    else byDelivery.set(key, [r]);
  }

  const groups: VehicleRowGroup[] = [];
  const reusable = reusableGroups(cache, vehicleExtraMap, driverNameById);
  // 今回の結果でキャッシュを作り直す。前回分を持ち越すのは「再利用したグループ」だけ
  const nextByGroupKey: VehicleRowGroupsCache['byGroupKey'] = new Map();
  for (const [groupKey, deliveryRows] of byDelivery) {
    const cached = reusable?.get(groupKey);
    if (cached && sameRows(cached.rows, deliveryRows)) {
      groups.push(cached.group);
      nextByGroupKey.set(groupKey, cached);
      continue;
    }
    const truckId = deliveryRows[0].truckId;
    const extra = vehicleExtraMap.get(truckId);
    const sortedRows = [...deliveryRows].sort((a, b) => a.rotationNumber - b.rotationNumber);

    // rowId が衝突する VM は先頭だけ残す。React key 衝突と dnd-kit droppable 重複を避ける。
    // rowId は deliveryId まで含むので、同じ回転番号でも delivery が違えば両方残る
    // （1 台に複数ドライバーが同じ時間帯で乗る勤務。rowIdFromTruck の説明参照）。
    const seenRowIds = new Set<string>();
    const uniqueRows = sortedRows.filter((vm) => {
      const rowId = rowIdFromTruck(truckId, vm.rotationNumber, vm.deliveryId);
      if (seenRowIds.has(rowId)) return false;
      seenRowIds.add(rowId);
      return true;
    });

    const allVehicleCards: DispatchCard[] = [];
    const rotationRows: RotationRow[] = uniqueRows.map((vm) => {
      const rowCards: DispatchCard[] = [];
      const deliveryCards: DeliveryCard[] = [];
      let cardIndex = 0;
      for (const src of vm.cards) {
        if (src.kind === 'loading') {
          rowCards.push(buildLoadingCard(src, truckId, vm.rotationNumber, cardIndex++));
        } else {
          const dc = buildDeliveryCard(src, truckId, vm.rotationNumber, cardIndex++);
          rowCards.push(dc);
          deliveryCards.push(dc);
        }
      }
      allVehicleCards.push(...rowCards);
      return {
        rowId: rowIdFromTruck(truckId, vm.rotationNumber, vm.deliveryId),
        vehicleId: vehicleIdFromTruckId(truckId),
        truckId,
        driverId: vm.driverId ?? null,
        // 勤務 (shift_truck) の id は delivery の id と同じ。placeholder 行は実体が無いので null
        shiftTruckId: vm.isPlaceholderRow ? null : vm.deliveryId,
        rotationNumber: vm.rotationNumber,
        totalRotations: vm.totalRotations,
        cards: rowCards,
        source: vm,
        deliveryCards,
      };
    });

    // 車番 (3 段目): 表示優先度は
    //   1. shift_trucks.truck_number (= 当日の運用上書き)
    //   2. leg_run.truck_number (= 実配車されている運用値・互換)
    //   3. trucks.truck_number (= マスタ最終フォールバック)
    // template カードの match candidate (= 同名未割当 legRun) は他車両に配車される可能性があり
    // ここで拾うと別車両の truck_number が混ざるため (2) は operations を持つカードに限定する。
    const plateNumberFromLegRun = uniqueRows
      .flatMap((r) => r.cards)
      .filter((c) => c.operations.length > 0)
      .flatMap((c) => c.legRuns)
      .map((lr) => lr.truck_number)
      .find((s) => typeof s === 'string' && s.trim().length > 0);
    const shiftTruckNumber = extra?.shiftTruckNumber;
    const plateNumber =
      (typeof shiftTruckNumber === 'string' && shiftTruckNumber.trim().length > 0
        ? shiftTruckNumber
        : null)
      ?? plateNumberFromLegRun
      ?? extra?.masterTruckNumber
      ?? '';

    // この truck × 全 rotation 配下で「実配車されている」 leg_run.id を集約 (車番一括更新で使う)。
    // template / no_delivery カードの legRuns は「同名の未割当 legRun」を候補として持つため、
    // そのまま含めると他の車両に割当される予定の legRun の車番まで書き換えてしまう (PM 報告のバグ)。
    // operations を 1 件以上持つカード (= 実 operation 由来) だけを対象にする。
    const legRunIds = Array.from(new Set(
      uniqueRows
        .flatMap((r) => r.cards)
        .filter((c) => c.operations.length > 0)
        .flatMap((c) => c.legRuns)
        .map((lr) => lr.id)
        .filter((id): id is number => typeof id === 'number'),
    ));

    const driverShift = extra?.shiftStart && extra?.shiftEnd
      ? { start: extra.shiftStart, end: extra.shiftEnd, workingAvailableSeconds: extra.workingAvailableSeconds ?? null }
      : null;

    // グループは勤務単位。deliveryIdOf は placeholder でも追加元 / truck の実在 delivery を
    // 返し、実体が無いときだけ負のダミーを返す
    const groupDeliveryId = deliveryIdOf(deliveryRows[0]);
    // 勤務由来の値は行から取る。extra は truckId 代表なので、1 台に複数の勤務が乗ると
    // 2 件目以降が 1 件目のドライバー・勤務になってしまう
    const groupDriverId = deliveryRows.map((r) => r.driverId).find((id) => id != null) ?? null;
    const groupDriverName = groupDriverId != null ? driverNameById?.get(groupDriverId) ?? null : null;

    const vehicle: Vehicle = {
      id: vehicleIdFromTruckId(truckId),
      companyName: extra?.partnerCompanyName ?? '',
      truckName: extra?.truckName ?? extra?.truckLabel ?? `トラック #${truckId}`,
      plateNumber,
      vehicleNumber: extra?.truckName ?? extra?.truckLabel ?? `トラック #${truckId}`,
      driverName: groupDriverName ?? extra?.defaultDriverName ?? '',
      driverShift,
      truckId,
      driverId: groupDriverId ?? extra?.driverId ?? null,
      shiftTruckId: groupDeliveryId > 0 ? groupDeliveryId : extra?.shiftTruckId ?? null,
      legRunIds,
      // マスタ未設定時は null (= 「キャパ不明」扱い)。0 にすると常時超過判定になる。
      maxCapacity: extra?.capacityQuantity ?? extra?.capacityCases ?? null,
      maxCapacityKg: extra?.capacityKg ?? null,
      maxCapacityM3: extra?.capacityM3 ?? null,
      color: extra?.truckColor ?? null,
      partnerCompanyId: extra?.partnerCompanyId ?? null,
      partnerCompanyColor: extra?.partnerCompanyColor ?? null,
      truckKlass: extra?.truckKlass ?? null,
      cards: allVehicleCards,
    };

    const group: VehicleRowGroup = {
      vehicle, rows: rotationRows, shiftTruckId: groupDeliveryId > 0 ? groupDeliveryId : null,
    };
    groups.push(group);
    nextByGroupKey.set(groupKey, { rows: deliveryRows, group });
  }
  if (cache) {
    cache.extraMap = vehicleExtraMap;
    cache.driverNameById = driverNameById;
    cache.byGroupKey = nextByGroupKey;
  }
  return groups;
}

/** ShipmentOrderLegRunForPlanEntity → UnassignedItem 変換 */
export function toUnassignedItem(lr: ShipmentOrderLegRunForPlanEntity, fallbackIndex: number): UnassignedItem {
  const id = lr.id != null ? `lr-${lr.id}` : `lr-idx-${fallbackIndex}`;
  const unloadingStayingMin = lr.unloading_staying_seconds != null && lr.unloading_staying_seconds > 0
    ? Math.round(lr.unloading_staying_seconds / 60)
    : null;

  return {
    id,
    storeName: lr.unloading_name || '—',
    storeAddress: lr.unloading_address,
    originAddress: lr.loading_address,
    geo: lr.unloading_lat != null && lr.unloading_lng != null ? { lat: lr.unloading_lat, lng: lr.unloading_lng } : undefined,
    cases: lr.item_count,
    weight: lr.item_total_weight_kg ?? null,
    volume: lr.item_total_volume_m3 ?? null,
    origin: lr.loading_name,
    destination: lr.unloading_name,
    originTimeWindow: formatTimeRange(lr.loading_start_at, lr.loading_end_at),
    destTimeWindow: formatTimeRange(lr.unloading_start_at, lr.unloading_end_at),
    shippers: lr.shipper_name ? [lr.shipper_name] : undefined,
    shipperBreakdown: buildShipperBreakdown([lr]),
    laneId: lr.shipment_order_lane_id ?? null,
    scheduledDate: formatJpDate(lr.unloading_start_at),
    scheduledTime: formatTimeRange(lr.unloading_start_at, lr.unloading_end_at),
    workDuration: unloadingStayingMin,
    invoiceCount: countInvoices([lr]),
    remarks: buildRemarks([lr]) ?? undefined,
    designatedTimeWindow: formatDesignatedWindow([lr], 'unloading'),
    loadingDesignatedTimeWindow: formatDesignatedWindow([lr], 'loading'),
    constraints: buildConstraintTags([lr]),
    loadingPrefecture: lr.loading_administrative_area_level1 ?? null,
    loadingCity: lr.loading_administrative_area_level2 ?? null,
    unloadingPrefecture: lr.unloading_administrative_area_level1 ?? null,
    unloadingCity: lr.unloading_administrative_area_level2 ?? null,
    wasSplit: lr.was_split,
    ...buildDisplayInfoFields([lr]),
    source: lr,
  };
}

/**
 * 「◯回転目」バッジに出す番号。グループ内の位置で 1 から数える。
 * ただし `displayRotationNumber` が付いている行はその番号を使う（行を落とす絞り込みが
 * 落とす前に確定させたもの）。
 *
 * グループは勤務 (delivery) ごとに分かれるので、グループ内は必ず同じ勤務になる。
 * shiftTruckId で分けて数えていたが、「回転を追加」の仮想行は shiftTruckId を
 * 持たない (null) ため、追加した回転だけ 1 回転目に戻っていた。
 *
 * Why not row.rotationNumber をそのまま出す: (a)「回転追加」で extraRotations に積まれた
 * 仮想行が server 側 cycle と独立した値を持つ、(b) FR placeholder を
 * hiddenPlaceholderRotations で隠したときにギャップが残る、の二つでずれる。
 */
export function buildDisplayRotationNumbers(
  rows: Pick<RotationRow, 'shiftTruckId' | 'displayRotationNumber'>[],
): number[] {
  return rows.map((row, index) => row.displayRotationNumber ?? index + 1);
}
